import {
  Button,
  Col,
  Divider,
  Flex,
  Form,
  Grid,
  Input,
  Row,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import bevzLogo from "../../assets/v2/bevzLogo.png";
import { validateEmail } from "../../components/apiRepository/DynamodbRepository";
import Footer from "../../components/v2/Footer/Footer";
import accountTypes from "../../constants/accountTypes";
import {
  ACTIONTYPES,
  useStore,
  useStoreDispatch,
} from "../../context/CreateStoreContext";
import style from "./Landing.module.css";

const { Text, Title, Paragraph } = Typography;
const { useBreakpoint } = Grid;

export default function Landing() {
  const { stepItems } = useStore();
  const dispatch = useStoreDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const screen = useBreakpoint();

  const onFinish = async (values) => {
    setLoading(true);

    const { emailAddress } = values;

    const response = await validateEmail({ emailAddress }).catch((_) => {
      form.setFields([
        {
          name: "emailAddress",
          errors: ["Error in email address. Please fix and try again."],
        },
      ]);
      return;
    });
    setLoading(false);
    if (response.data.emailExists) {
      form.setFields([
        {
          name: "emailAddress",
          errors: ["Email address already exists."],
        },
      ]);

      return;
    }

    dispatch({ type: ACTIONTYPES.CLEAR_FORM_STATES });
    setTimeout(() => {
      dispatch({ type: ACTIONTYPES.ACCOUNTTYPE, data: accountTypes.owner });
      dispatch({ type: ACTIONTYPES.ACCOUNT, data: values });
      dispatch({
        type: ACTIONTYPES.STEPS,
        data: {
          currentIndex: 1,
          lastlastStepIndex: 1,
          stepItems: stepItems.map((step, index) => {
            if (index === 0)
              return { ...step, disabled: false, status: "finish" };
            if (index === 1)
              return { ...step, disabled: false, status: "process" };
            return step;
          }),
        },
      });

      navigate("/create");
    }, 500);
  };

  useEffect(() => {
    dispatch({ type: ACTIONTYPES.CLEARSTATES });
  }, [dispatch]);

  return (
    <>
      <Row style={{ minHeight: "calc(100vh - 32px)" }}>
        <Col xs={{ span: 24, order: 2 }} lg={{ span: 15, order: 1 }} xl={14}>
          <Row style={{ height: "100%" }}>
            <Col
              xs={{ span: 18, offset: 3 }}
              lg={{ span: 12, offset: 8 }}
              xl={{ span: 8, offset: 12 }}
            >
              <Flex
                vertical
                align="center"
                justify="space-between"
                className={style.Form}
              >
                <Flex
                  align="center"
                  justify="center"
                  vertical
                  flex={1}
                  className={style.Container}
                >
                  <img
                    src={bevzLogo}
                    width="171px"
                    height="115px"
                    className="BevzLogo"
                  />
                  <Title level={screen.xs ? 3 : 2} className={style.Title}>
                    Get Started
                  </Title>
                  <Paragraph className={style.SubTitle}>
                    Create your first store and simplify your delivery process!
                  </Paragraph>
                  <Form
                    form={form}
                    onFinish={onFinish}
                    layout="vertical"
                    className={style.form}
                    requiredMark={false}
                    style={{ width: "100%" }}
                  >
                    <Form.Item
                      name="emailAddress"
                      label="Email Address"
                      required
                      rules={[
                        {
                          required: true,
                          message: "This is a required field.",
                        },
                        {
                          type: "email",
                          message: "Please enter valid email address.",
                        },
                      ]}
                    >
                      <Input placeholder="your@email.com" size="large" />
                    </Form.Item>

                    <Form.Item
                      hasFeedback
                      name="password"
                      label="Password"
                      required
                      rules={[
                        {
                          required: true,
                          message: "This is a required field.",
                        },
                        {
                          min: 8,
                          message: "Password must be at least 8 characters.",
                        },
                      ]}
                    >
                      <Input.Password placeholder="" size="large" />
                    </Form.Item>

                    <Form.Item
                      dependencies={["password"]}
                      hasFeedback
                      name="confirmpassword"
                      label="Confirm Password"
                      required
                      rules={[
                        {
                          required: true,
                          message: "This is a required field.",
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue("password") === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error("Password does not match!"),
                            );
                          },
                        }),
                      ]}
                      style={{ marginBottom: "18px" }}
                    >
                      <Input.Password placeholder="" size="large" />
                    </Form.Item>
                    <div style={{ textAlign: "center", minHeight: "22px" }}>
                      {error ? (
                        <Text type="danger" strong>
                          {error}
                        </Text>
                      ) : null}
                    </div>

                    <Form.Item style={{ marginBottom: 0 }}>
                      <Button
                        type="primary"
                        htmlType="submit"
                        className={style.form_button}
                        iconPosition="end"
                        loading={loading}
                      >
                        Proceed to Create Account
                      </Button>
                    </Form.Item>
                  </Form>

                  <Divider
                    plain
                    style={{ marginTop: "2.5rem", marginBottom: "2.5rem" }}
                  >
                    or
                  </Divider>

                  <Paragraph
                    style={{
                      margin: 0,
                      color: "#3d3d3d",
                      textAlign: "center",
                      fontSize: screen.xs ? "inherit" : "16px",
                    }}
                  >
                    Adding new stores to your existing account?
                    <br />
                    <Link to="login" style={{ fontWeight: "600" }}>
                      Login
                    </Link>
                  </Paragraph>
                </Flex>

                <Paragraph className={style.ContactUs}>
                  For any questions, contact us at{" "}
                  <a href="mailto:support@bevz.com">support@bevz.com</a>
                </Paragraph>
              </Flex>
            </Col>
          </Row>
        </Col>
        <Col
          xs={{ span: 22, offset: 1, order: 1 }}
          lg={{ span: 9, offset: 0, order: 2 }}
          xl={10}
          className={style.Right}
        >
          <Row
            align={{ xs: "middle", md: "middle", lg: "top" }}
            justify={{ xs: "center" }}
            style={{ height: "100%" }}
          >
            <Col
              xs={{ span: 24, offset: 0 }}
              md={{ span: 15, offset: 7 }}
              lg={{ span: 15, offset: 2 }}
              className={style.container}
            >
              <Title className={style.Title}>Join the Bevz Buzz</Title>
              <Paragraph className={style.SubTitle}>
                Say goodbye to the hassle of managing multiple tablets and hello
                to increased revenue.
              </Paragraph>
            </Col>
          </Row>
        </Col>
      </Row>
      <Footer />
    </>
  );
}
